import { useMemo, useState } from "react";

import clsx from "clsx";
import copy from "copy-to-clipboard";
import { orderBy } from "lodash";
import { useToasts } from "react-toast-notifications";

import { GenericTooltip } from "@components/tooltip-container";
import { TertiaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";

import {
  type AdvancedSegmentDescriptor,
  type AdvancedSegmentLevel,
  type Sorting,
} from "../types";

import { HeaderMenu } from "./header-menu";
import { SortDirection } from "./sort-direction";

const LEVEL_LABEL: Record<AdvancedSegmentLevel, string> = {
  campaign: "Campaign",
  adset: "Adset",
  ad: "Ad",
};

interface Props {
  segments: AdvancedSegmentDescriptor[];
  onChange: (value: AdvancedSegmentDescriptor) => void;
  onEdit: (value: AdvancedSegmentDescriptor) => void;
  onDuplicate: (value: AdvancedSegmentDescriptor) => void;
  onDelete: (value: AdvancedSegmentDescriptor) => void;
}

export const Table = ({
  segments,
  onChange,
  onEdit,
  onDuplicate,
  onDelete,
}: Props) => {
  const { addToast } = useToasts();

  const [sorting, setSorting] = useState<Sorting>({
    column: "name",
    order: "asc",
  });

  const rows = useMemo(() => {
    return orderBy(segments, sorting.column, sorting.order);
  }, [segments, sorting]);

  return (
    <table className="text-xs table-fixed	border-spacing-0 border-separate min-w-[100%]">
      <thead className="sticky top-0 bg-white z-[9]">
        <tr>
          <th className="h-[48px] w-[70px] min-w-[70px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1 text-center">Active</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] w-[220px] min-w-[200px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Segment name</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="name" onChange={setSorting} />
                <SortDirection
                  name="name"
                  sorting={sorting}
                  onClick={setSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] w-[110px] min-w-[110px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Level</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="level" onChange={setSorting} />
                <SortDirection
                  name="level"
                  sorting={sorting}
                  onClick={setSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] w-[90px] min-w-[90px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Rank</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="priority" onChange={setSorting} />
                <SortDirection
                  name="priority"
                  sorting={sorting}
                  onClick={setSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] min-w-[120px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">
                Rules
                <p className="font-normal text-xxs text-gray m-0">
                  Created from character combinations used in campaign/adset/ad
                  names
                </p>
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] w-[220px] min-w-[200px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Output label name</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="output" onChange={setSorting} />
                <SortDirection
                  name="output"
                  sorting={sorting}
                  onClick={setSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[48px] w-[260px] min-w-[260px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Actions</div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          return (
            <tr key={i} className="group odd:bg-[#fafafe] even:bg-white">
              <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                <div className="font-bold p-2 flex items-center justify-center">
                  <Switch
                    id={`switch-active-${row.id}`}
                    value={row.active}
                    onChange={(value) => onChange({ ...row, active: value })}
                  />
                </div>
              </td>
              <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                <div className="font-bold p-2 text-sm">{row.name}</div>
              </td>
              <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                <div className="p-2 text-sm">{LEVEL_LABEL[row.level]}</div>
              </td>
              <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                <div className="p-2 text-right text-sm">{row.priority}</div>
              </td>
              <td
                className={clsx(
                  "border-r border-gray-2 align-top border-b group-last:border-b-0",
                  "relative group/copy truncate max-w-[1px]",
                )}
              >
                <div className="p-2 truncate">
                  <div className="truncate mr-8 flex">
                    <span className="inline-block font-mono text-xs text-venus w-full truncate">
                      {JSON.stringify(row.matcher)}
                    </span>
                    <GenericTooltip
                      infoCircleClassName="align-[3px]"
                      content={
                        <pre
                          className={clsx(
                            "font-mono text-xs text-venus max-h-[400px] w-[700px] overflow-auto",
                          )}
                        >
                          {JSON.stringify(row.matcher, null, 2)}
                        </pre>
                      }
                    ></GenericTooltip>
                  </div>
                </div>
                <div
                  className={clsx(
                    "absolute top-0 right-[8px] h-full flex items-center",
                    "invisible group-hover/copy:visible",
                  )}
                >
                  <GenericTooltip
                    content="Copy"
                    noInfoCircle
                    overlayClassName="pointer-events-none"
                  >
                    <i
                      className={clsx(
                        "bg-white elevation-1 rounded-full p-[5px] fa-sharp fa-solid fa-clone",
                        "cursor-pointer text-md text-primary font-light",
                      )}
                      onClick={() => {
                        addToast("COPIED TO CLIPBOARD", {
                          appearance: "success",
                        });

                        copy(JSON.stringify(row.matcher));
                      }}
                    ></i>
                  </GenericTooltip>
                </div>
              </td>
              <td className="border-r border-gray-2 align-top border-b group-last:border-b-0">
                <div className="p-2 font-mono text-venus">
                  {row.output.trim()}
                </div>
              </td>
              <td className="border-gray-2 border-b align-top group-last:border-b-0">
                <div className="flex items-center gap-2 p-2">
                  <TertiaryButton
                    className={clsx(
                      "flex items-center justify-center gap-2 bg-mars-2 hover:bg-[#fce2db]",
                      "rounded-md font-bold m-0 p-0 h-8 w-8",
                    )}
                    onClick={() => onDelete(row)}
                  >
                    <i className="fa-regular fa-trash text-mars" />
                  </TertiaryButton>
                  <TertiaryButton
                    className={clsx(
                      "flex items-center gap-2 font-bold bg-cosmic-4 hover:bg-cosmic-3",
                      "rounded-md",
                    )}
                    onClick={() => {
                      onDuplicate(row);
                    }}
                  >
                    <i className="fa-regular fa-copy" /> DUPLICATE
                  </TertiaryButton>
                  <TertiaryButton
                    className={clsx(
                      "flex items-center gap-2 font-bold bg-cosmic-4 hover:bg-cosmic-3",
                      "rounded-md",
                    )}
                    onClick={() => onEdit(row)}
                  >
                    <i className="fa-regular fa-pen" /> EDIT
                  </TertiaryButton>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
